/**
 * FERP Portal - Focus Banner
 *
 * DRR-178 Phase 3: Persistent banner showing the currently focused actor.
 * Appears below the portal header when a focus context is active.
 * Click "x" to clear focus and return to unscoped view.
 *
 * Usage:
 * Place in the portal layout (e.g., AppShell.Main wrapper):
 * ```tsx
 * <FocusBanner />
 * <Outlet />
 * ```
 */

import { Group, Text, Badge, CloseButton, Paper } from '@mantine/core';
import { IconFocus2 } from '@tabler/icons-react';
import { useFocusContext } from '@/contexts/FocusContext';

export function FocusBanner() {
  const { isFocused, focusedDisplayName, perspective, clearFocus } = useFocusContext();

  if (!isFocused) return null;

  const perspectiveLabel = perspective === 'performer' ? 'Viewing work by' : 'Focused on';

  return (
    <Paper
      p="xs"
      radius={0}
      data-testid="focus-banner"
      style={{
        borderBottom: '2px solid var(--mantine-color-blue-5)',
        backgroundColor: 'var(--mantine-color-blue-0)',
      }}
    >
      <Group gap="sm" justify="center">
        <IconFocus2 size={16} color="var(--mantine-color-blue-6)" />
        <Text size="sm" fw={500}>
          {perspectiveLabel}:
        </Text>
        <Badge variant="filled" color="blue" size="md" data-testid="focus-banner-name">
          {focusedDisplayName}
        </Badge>
        <Text size="xs" c="dimmed">
          (CRITICAL events always visible)
        </Text>
        <CloseButton
          size="sm"
          aria-label="Clear focus"
          onClick={clearFocus}
          data-testid="focus-banner-clear"
        />
      </Group>
    </Paper>
  );
}
